import { Horizontal, Texto, Vertical, GraviButton } from '@gravitate-js/excalibrr'
import { Col, DatePicker, Drawer, Form, InputNumber, Row, Select, Switch } from 'antd'
import * as React from 'react'
import { useEffect } from 'react'
import type { CreateSurchargeDrawerProps } from '../types'
import { useCreateSurcharge } from '../hooks'
import { useRateManagement as useApiRateManagement } from '@api/useFreight/useFreight'

/**
 * Component for creating or editing surcharges
 */
export function CreateSurchargeDrawer({
  ratebook,
  visible,
  onClose,
  onSuccess,
  editingSurcharge,
}: CreateSurchargeDrawerProps) {
  const {
    form,
    hasEndDate,
    setHasEndDate,
    submitting,
    rateType,
    initializeForm,
    handleSubmit,
    handleFormSubmit,
    handleClose,
  } = useCreateSurcharge(onSuccess, onClose, ratebook)

  const { useFilteredMetadataQuery } = useApiRateManagement()
  const { data: metadataOptions } = useFilteredMetadataQuery(ratebook)

  // Initialize form when visibility or editing surcharge changes
  useEffect(() => {
    initializeForm(visible, editingSurcharge)
  }, [visible, editingSurcharge, initializeForm])

  return (
    <Drawer
      title={editingSurcharge ? 'Edit Surcharge' : 'Create Surcharge'}
      open={visible}
      onClose={handleClose}
      width={400}
      styles={{
        body: {
          padding: 0,
        },
      }}
      footer={
        <Horizontal justifyContent='flex-end' gap={8}>
          <GraviButton appearance='outlined' onClick={handleClose}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 onClick={handleFormSubmit} loading={submitting}>
            {editingSurcharge ? 'Update' : 'Create'}
          </GraviButton>
        </Horizontal>
      }
    >
      <Form
        form={form}
        layout='vertical'
        style={{ backgroundColor: 'var(--bg-1)', height: '100%' }}
        onFinish={(values) => handleSubmit(values, ratebook, editingSurcharge)}
        initialValues={{
          effective_from: dayjs(),
          type: 'Percentage',
        }}
      >
        <Vertical className='p-3' bg='default' gap={16}>
          <Form.Item name='freight_region' label='Freight Region'>
            <Select
              allowClear
              placeholder='Any'
              options={metadataOptions?.freight_regions
                ?.map((region: any) => ({
                  label: region.name,
                  value: region.name,
                  region_id: region.id,
                }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
              onChange={(value, option: any) => {
                if (value && option) {
                  form.setFieldValue('freight_region_id', option.region_id)
                } else {
                  form.setFieldValue('freight_region_id', null)
                }
              }}
            />
          </Form.Item>

          <Form.Item name='product_group' label='Product Group'>
            <Select
              allowClear
              placeholder='Any'
              options={metadataOptions?.product_groups
                ?.map((product: string) => ({ label: product, value: product }))
                .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name='type' label='Rate Type' rules={[{ required: true, message: 'Rate Type is required' }]}>
                <Select
                  options={[
                    { label: 'Percentage', value: 'Percentage' },
                    { label: 'Per Mile', value: 'Per Mile' },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              {!editingSurcharge && (
                <Form.Item
                  name='surcharge'
                  label='Surcharge Rate'
                  rules={[{ required: true, message: 'Surcharge rate is required' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    defaultValue={0}
                    precision={rateType === 'Percentage' ? 3 : 5}
                    placeholder='Enter Rate'
                    prefix={rateType === 'Per Mile' ? '$' : undefined}
                    suffix={rateType === 'Percentage' ? '%' : 'Per mile'}
                    stringMode
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {!editingSurcharge && (
            <>
              <Form.Item
                name='effective_from'
                label='Effective From'
                rules={[
                  {
                    required: !editingSurcharge,
                    message: 'Effective From date is required',
                  },
                ]}
              >
                <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Horizontal verticalCenter gap={8}>
                  <Switch checked={hasEndDate} onChange={setHasEndDate} />
                  <Texto>{hasEndDate ? 'End Date' : 'No End Date'}</Texto>
                </Horizontal>
              </Form.Item>

              {hasEndDate && (
                <Form.Item
                  name='effective_to'
                  label='Effective To'
                  rules={[
                    { required: hasEndDate, message: 'Please select end date' },
                    {
                      validator: (_, value) => {
                        if (
                          hasEndDate &&
                          value &&
                          form.getFieldValue('effective_from') &&
                          value.isBefore(form.getFieldValue('effective_from'))
                        ) {
                          return Promise.reject('End date must be after start date')
                        }
                        return Promise.resolve()
                      },
                    },
                  ]}
                >
                  <DatePicker style={{ width: '100%' }} format='MM/DD/YYYY' />
                </Form.Item>
              )}
            </>
          )}
        </Vertical>
      </Form>
    </Drawer>
  )
}

// Import dayjs separately to avoid TypeScript errors
import dayjs from 'dayjs'
